import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "../../components/AppIcon";
import { useAuth } from "../../contexts/AuthContext";

import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { Checkbox } from "../../components/ui/Checkbox";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, isAuthenticated, userRole, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    userId: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    // Only navigate if we're still on the login page, have a role, and haven't navigated yet
    if (isAuthenticated && userRole && location.pathname === "/login") {
      console.log("userRole", userRole);
      if (userRole === "super_admin" || userRole === "project_admin") {
        navigate("/host-dashboard", { replace: true });
      } else if (userRole === "cluster_manager") {
        navigate("/cluster-dashboard", { replace: true });
      } else if (userRole === "worker") {
        navigate("/worker-dashboard", { replace: true });
      } else {
        navigate("/unauthorized", { replace: true });
      }
    }
  }, [isAuthenticated, userRole, location.pathname, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.userId?.trim()) {
      newErrors.userId = "User ID is required";
    } else if (!/^[A-Z]{1,3}[0-9]{1,4}$/i.test(formData?.userId?.trim())) {
      newErrors.userId = "Please enter a valid User ID (e.g., UA001, UPA002)";
    }

    if (!formData?.password) {
      newErrors.password = "Password is required";
    } else if (formData?.password?.length < 4) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      rememberMe: e?.target?.checked,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await signIn(
        formData.userId.trim().toUpperCase(),
        formData.password,
      );

      if (result.success) {
        // Store remember me preference
        if (formData.rememberMe) {
          localStorage.setItem(
            "rememberedUser",
            formData.userId.trim().toUpperCase(),
          );
        } else {
          localStorage.removeItem("rememberedUser");
        }

        // Redirect based on user role
        const role = result.user?.role;
        if (role === "super_admin" || role === "project_admin") {
          navigate("/host-dashboard");
        } else if (role === "cluster_manager") {
          navigate("/cluster-dashboard");
        } else if (role === "worker") {
          navigate("/worker-dashboard");
        } else {
          navigate("/unauthorized");
        }
      } else {
        setErrors({
          submit:
            result.error ||
            "Invalid credentials. Please check your User ID and password.",
        });
      }
    } catch (error) {
      setErrors({
        submit:
          error.message ||
          "An error occurred during sign in. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8 md:py-12">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8 lg:p-10">
          <div className="flex flex-col items-center mb-6 md:mb-8">
            <div className="w-32 h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-2xl flex items-center justify-center mb-4">
              <img
                src="/assets/images/brand.png"
                alt="Valence Energies logo featuring stylized 've' monogram in vibrant green color with modern three-dimensional design"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground text-center">
              Valence Energies
            </h1>
            <p className="text-sm md:text-base text-muted-foreground text-center mt-2">
              Solar Installation Management System
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            <Input
              label="User ID"
              type="text"
              name="userId"
              placeholder="Enter your User ID (e.g., UA001, UPA002)"
              value={formData?.userId}
              onChange={handleInputChange}
              error={errors?.userId}
              required
              disabled={isLoading}
              autoCapitalize="characters"
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                value={formData?.password}
                onChange={handleInputChange}
                error={errors?.password}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>

            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Icon
                    name="AlertCircle"
                    size={20}
                    color="var(--color-error)"
                    className="flex-shrink-0 mt-0.5"
                  />
                  <p className="text-sm text-error whitespace-pre-line">
                    {errors?.submit}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-start gap-3">
              <Checkbox
                label="Remember me"
                checked={formData?.rememberMe}
                onChange={handleCheckboxChange}
                disabled={isLoading}
              />
            </div>

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              iconName="LogIn"
              iconPosition="right"
              className="mt-6"
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 md:mt-8 pt-6 border-t border-border">
            <div className="flex flex-col items-center gap-4">
              <p className="text-xs md:text-sm text-muted-foreground text-center">
                Trusted by solar installation teams across India
              </p>
              <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <Icon name="Shield" size={16} color="var(--color-success)" />
                  <span className="text-xs text-muted-foreground">
                    Aadhaar Verified
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    name="CheckCircle2"
                    size={16}
                    color="var(--color-success)"
                  />
                  <span className="text-xs text-muted-foreground">
                    Govt. Compliant
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Award" size={16} color="var(--color-success)" />
                  <span className="text-xs text-muted-foreground">
                    ISO Certified
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              &copy; {new Date()?.getFullYear()} Valence Energies. All rights
              reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
